'use client'

/**
 * MobileChatPane — full-viewport chat experience for the mobile lobby's
 * second swipe pane.
 *
 * Mirrors the data wiring of `CampaignChatPanel` but drops every desktop-only
 * concern: no resize handle, no collapsed rail, no fullscreen toggle, no
 * dock side. It just renders `<ChatInterface variant="fullscreen">` with the
 * same `useChat()` state the desktop panel reads, so threads, seed messages,
 * pending field/event contexts, and onboarding state all stay in sync as
 * the user swipes between the app pane and the chat pane.
 *
 * Swipe-aware behavior:
 *  - The pane is wrapped in `MobileSwipePanes`, which controls visibility.
 *  - `panelActive` is true only when the chat pane is the active panel; this
 *    keeps the `ChatInterface` from auto-sending seed messages or stealing
 *    focus while the user is on the app pane.
 *  - The header back chevron calls `onBack`, which the swipe shell wires to
 *    `closeChat()` (router.back when possible).
 */

import { useCallback, useMemo } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useChat } from '@/contexts/ChatContext'
import { useCurrentWorkspace } from '@/hooks/useCurrentWorkspace'
import { ChatInterface } from '@/components/chat/ChatInterface'
import { cn } from '@/lib/utils'

export type MobileChatPaneProps = {
  /** True when this pane is the user-visible panel. Pauses input focus / auto-send when false. */
  active: boolean
  /** Handler for the header back chevron. The swipe shell wires this to closeChat(). */
  onBack: () => void
}

export function MobileChatPane({ active, onBack }: MobileChatPaneProps) {
  const {
    flowIntent,
    seedMessage,
    autoSendSeed,
    clearAutoSendSeed,
    openChat,
    currentThreadId,
    setCurrentThreadId,
    pendingFieldContext,
    setPendingFieldContext,
    pendingFieldContexts,
    setPendingFieldContexts,
    pendingEventContext,
    setPendingEventContext,
    flagUrlChangeSource,
    isOnboarding,
  } = useChat()
  const router = useRouter()
  // usePathname() can briefly be null/undefined during hydration (Next 15 + parallel routes).
  const pathname = usePathname() ?? ''
  const { workspace, isLoading: workspaceLoading } = useCurrentWorkspace()
  const isOnChatRoute = pathname === '/chat'
  const effectiveFlowIntent = isOnChatRoute && isOnboarding ? 'onboarding' : flowIntent

  const endpointBody = useMemo(
    () => ({
      flowIntent: effectiveFlowIntent,
      eventContext: pendingEventContext || undefined,
      pathname,
      workspaceId: workspace?.id,
    }),
    [effectiveFlowIntent, pendingEventContext, pathname, workspace?.id],
  )

  const apiMessageTransform = useCallback(
    (userMessage: string): string => {
      const activeContexts =
        pendingFieldContexts.length > 0
          ? pendingFieldContexts
          : pendingFieldContext
            ? [pendingFieldContext]
            : []
      if (!activeContexts.length) return userMessage
      const contextLines = activeContexts.map((ctx) => {
        const fieldLabel = ctx.field.replace(/_/g, ' ')
        return ctx.currentValue
          ? `• "${fieldLabel}": "${ctx.currentValue}"`
          : `• "${fieldLabel}"`
      })
      setPendingFieldContext(null)
      setPendingFieldContexts([])
      return `[Context: Discussing the following brand fields]\n${contextLines.join('\n')}\n\n${userMessage}`
    },
    [
      pendingFieldContexts,
      pendingFieldContext,
      setPendingFieldContext,
      setPendingFieldContexts,
    ],
  )

  const handleSlashCommand = useCallback(
    (intent: string) => {
      openChat(intent as 'campaign' | 'video' | 'brand')
    },
    [openChat],
  )

  // Mirrors the navigate handler from CampaignChatPanel, minus the
  // panel-fullscreen exits (we have no fullscreen state here). Tool-driven
  // navigations swipe the user back to the app pane automatically because
  // they push to `/<route>` and our shell reads `?panel` from the URL.
  const handleNavigate = useCallback(
    (url: string) => {
      setPendingEventContext(null)
      flagUrlChangeSource('tool-navigation')
      const target =
        url === '/wallet' || url.startsWith('/wallet?')
          ? url.replace('/wallet', '/budget')
          : url
      router.push(target)
    },
    [flagUrlChangeSource, router, setPendingEventContext],
  )

  const handleUiEvent = useCallback(
    (_event: Record<string, unknown>) => {
      // Mobile chat does not surface side-rail UI events at this stage; the
      // existing CampaignChatPanel handles event-driven side panel UX. If we
      // need parity later we can fan these into the same channels.
    },
    [],
  )

  const handleThreadCreated = useCallback(
    (threadId: string, _title: string) => {
      setCurrentThreadId(threadId)
    },
    [setCurrentThreadId],
  )

  const onboardingKickoffEndpoint =
    isOnChatRoute && isOnboarding ? '/api/onboarding/kickoff' : null

  return (
    <div
      data-mobile-chat-pane
      className={cn(
        'flex h-full w-full shrink-0 flex-col bg-atrium-background',
        // ChatInterface paints its own header in fullscreen+mobileLayout mode
        // (with chevron-back), so we don't render an extra MobileHeader here.
        // We DO want the safe-area-top inset because ChatInterface's header
        // doesn't apply it; padding here keeps the chat header below the notch.
        'pt-[env(safe-area-inset-top)]',
      )}
    >
      <ChatInterface
        endpoint="/api/campaign-chat"
        endpointBody={endpointBody}
        workspaceId={workspace?.id}
        workspaceReady={!workspaceLoading}
        flowIntent={effectiveFlowIntent}
        seedMessage={seedMessage}
        autoSendSeed={autoSendSeed && active}
        onAutoSendConsumed={clearAutoSendSeed}
        onboardingKickoffEndpoint={onboardingKickoffEndpoint}
        threadId={currentThreadId}
        onThreadCreated={handleThreadCreated}
        onNavigate={handleNavigate}
        onUiEvent={handleUiEvent}
        apiMessageTransform={apiMessageTransform}
        extractEndpoint={
          currentThreadId ? `/api/chat/threads/${currentThreadId}/extract` : null
        }
        forceNewThread={!!seedMessage}
        onSlashCommand={handleSlashCommand}
        variant="fullscreen"
        mobileLayout
        panelActive={active}
        onCollapse={onBack}
        className="min-h-0 flex-1"
      />
    </div>
  )
}
